import {Box} from '@primer/react'
import type {BetterSystemStyleObject} from '@primer/react/lib-esm/sx'
import {useMemo} from 'react'
import {TimelineDivider} from './TimelineDivider'

type TimelineRowBorderProps = {
  children: React.ReactNode
  addDivider: boolean
  item: {
    __id: string
  }
  isMajor?: boolean
  isHighlighted?: boolean
  sx?: BetterSystemStyleObject
  commentParams?: TimelineRowBorderCommentParams
}

export type TimelineRowBorderCommentParams = {
  first: boolean
  last: boolean
}

const sharedStyling: BetterSystemStyleObject = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  border: '1px solid',
  borderColor: 'transparent',
  borderRadius: 2,
  backgroundColor: 'canvas.default',
}

export const TimelineRowBorder = (props: TimelineRowBorderProps) => {
  const {children, addDivider, item, isMajor, isHighlighted, commentParams, sx} = props

  const highlightedStyling = useMemo(
    () =>
      isHighlighted
        ? {
            border: '1px solid',
            borderColor: 'accent.fg',
            boxShadow: `0px 0px 0px 1px var(--color-accent-fg), 0px 0px 0px 4px var(--color-accent-subtle)`,
          }
        : {},
    [isHighlighted],
  )

  const commentStyling = useMemo(
    () =>
      commentParams
        ? {
            ...(!commentParams.first ? {marginTop: '8px'} : {}),
            ...(!commentParams.last ? {marginBottom: '8px'} : {}),
          }
        : {},
    [commentParams],
  )

  const majorEventStyling = useMemo(() => {
    return {
      ...sharedStyling,
      borderColor: 'border.default',
      boxShadow: 'shadow.small',
      py: 2,
      ...commentStyling,
      ...highlightedStyling,
      ...sx,
    }
  }, [commentStyling, highlightedStyling, sx])

  const defaultStyling = useMemo(() => {
    return {
      ...sharedStyling,
      ...sx,
    }
  }, [sx])

  return (
    <>
      {addDivider && <TimelineDivider id={item.__id} />}
      <Box
        key={item.__id}
        sx={isMajor ? majorEventStyling : defaultStyling}
        data-testid={`timeline-row-border-${item.__id}`}
      >
        {children}
      </Box>
    </>
  )
}

try{ TimelineRowBorder.displayName ||= 'TimelineRowBorder' } catch {}